import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CachedIcon from "@mui/icons-material/Cached";
import { fonts, size, colors } from "../../ColorFont/color";
import { url } from "../../ConstUrl/Url";
import { Add } from "@mui/icons-material";

const SellerPlan = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [plans, setPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [loading, setLoading] = useState(true); // State to handle loading state
  const [error, setError] = useState(null); // State to handle error
  const [isOpen, setIsOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [newPlan, setNewPlan] = useState({
    name: "",
    originalPrice: "",
    discountedPrice: "",
    sponseredHrs: "",
  });

  const updateFormRef = useRef(null);

  // Fetch all plans
  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch(`${url}/api/get-seller-plans`);
      const data = await response.json();
      setPlans(data);
    } catch (error) {
    } finally {
      setLoading(false); // Set loading to false after the request completes
    }
  };

  // Handle editing a plan
  const handleEdit = (plan) => {
    setIsOpen(true);
    setEditingPlan({ ...plan });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewPlan({ ...newPlan, [name]: value });
  };

  const handleAdd = async () => {
    try {
      const response = await fetch(`${url}/api/add-seller-plans`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(newPlan),
      });

      if (response.ok) {
        fetchPlans();
        setNewPlan({
          name: "",
          originalPrice: "",
          discountedPrice: "",
          sponseredHrs: "",
        });
      }
    } catch (error) {
      console.error(error);
    }
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
    setNewPlan({
      name: "",
      originalPrice: "",
      discountedPrice: "",
      sponseredHrs: "",
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingPlan({ ...editingPlan, [name]: value });
  };

  const handleSave = async () => {
    try {
      const response = await fetch(
        `${url}/api/update-seller-plans/${editingPlan._id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(editingPlan),
        }
      );

      if (response.ok) {
        fetchPlans();
        setEditingPlan(null);
      }
    } catch (error) {}
  };

  const handleDelete = async (id) => {
    try {
      const response = await fetch(`${url}/api/delete-seller-plans/${id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        fetchPlans();
      }
    } catch (error) {}
  };

  const handleClickOpen = () => {
    setOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ margin: "14px" }}>
        <Skeleton variant="text" sx={{ widows: 300, height: 40 }} />
        <Paper sx={{ padding: "14px", marginTop: 2 }}>
          <Skeleton variant="text" sx={{ widows: 200, height: 40 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Skeleton variant="text" sx={{ widows: 300, height: 50 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Skeleton variant="text" sx={{ widows: 300, height: 50 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Skeleton variant="text" sx={{ widows: 300, height: 50 }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Skeleton variant="text" sx={{ widows: 300, height: 50 }} />
            </Grid>
            <Grid item xs={12}>
              <Skeleton variant="text" sx={{ widows: 600, height: 50 }} />
            </Grid>
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-end",
                alignItems: "center",
              }}
            >
              <Button>
                <Skeleton variant="text" sx={{ widows: 100, height: 50 }} />
              </Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper
          sx={{
            padding: "14px",
            marginTop: 2,
            marginTop: 8,
          }}
        >
          <Skeleton variant="text" sx={{ widows: 200, height: 40 }} />

          {plans.map((plan) => (
            <Box key={plan._id} sx={{ marginBottom: 2, padding: "14px" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Skeleton variant="text" sx={{ widows: 200, height: 40 }} />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <Box>
                  <Skeleton variant="text" sx={{ widows: 200, height: 40 }} />

                  <Skeleton variant="text" sx={{ widows: 200, height: 40 }} />
                </Box>

                <Box>
                  <Skeleton variant="text" sx={{ widows: 200, height: 40 }} />

                  <Skeleton variant="text" sx={{ widows: 200, height: 40 }} />
                </Box>
              </Box>
            </Box>
          ))}
        </Paper>

        {editingPlan && (
          <Paper ref={updateFormRef} sx={{ padding: "14px", marginTop: 2 }}>
            <Skeleton variant="text" sx={{ widows: 250, height: 40 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Skeleton variant="text" sx={{ widows: 300, height: 50 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Skeleton variant="text" sx={{ widows: 300, height: 50 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Skeleton variant="text" sx={{ widows: 300, height: 50 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Skeleton variant="text" sx={{ widows: 300, height: 50 }} />
              </Grid>
              <Grid item xs={12}>
                <Skeleton variant="text" sx={{ widows: 600, height: 50 }} />
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              >
                <Button>
                  <Skeleton variant="text" sx={{ widows: 100, height: 50 }} />
                </Button>
              </Grid>
            </Grid>
          </Paper>
        )}
      </Box>
    );
  }

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <Box sx={{ margin: "14px" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          marginBottom: 5,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography
          sx={{
            fontFamily: fonts.poppins,
            fontWeight: 600,
            fontSize: isMobile ? size.mobileTitle : size.title,
            color: colors.primary_dark,
          }}
        >
          Manage Plans For Seller
        </Typography>

        <Button
          variant="contained"
          onClick={handleClickOpen}
          sx={{
            fontFamily: fonts.poppins,
            backgroundImage: colors.button,
            width: isMobile ? "100%" : "auto",
            padding: "8px 20px",
            borderRadius: 2,
          }}
        >
          Add New Plan
        </Button>
      </Box>

      <Box>
        <Dialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              padding: isMobile ? 2 : 3,
              boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
            },
          }}
        >
          <DialogTitle
            sx={{
              fontFamily: fonts.poppins,
              fontSize: isMobile ? size.mobileSubtitle : size.subtitle,
              color: colors.primary_dark,
              textAlign: "center",
              marginBottom: 2,
            }}
          >
            Edit Plan
          </DialogTitle>

          {editingPlan && (
            <DialogContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    label="Plan Name"
                    name="name"
                    value={editingPlan.name}
                    onChange={handleEditChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    label="Original Price"
                    name="originalPrice"
                    value={editingPlan.originalPrice}
                    onChange={handleEditChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    label="Discounted Price"
                    name="discountedPrice"
                    value={editingPlan.discountedPrice}
                    onChange={handleEditChange}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    label="No of Hours"
                    name="sponseredHrs"
                    value={editingPlan.sponseredHrs}
                    onChange={handleEditChange}
                    fullWidth
                  />
                </Grid>
              </Grid>
            </DialogContent>
          )}

          <DialogActions
            sx={{
              justifyContent: "flex-end",
              flexDirection: isMobile ? "column" : "row",
              gap: isMobile ? 1 : 0,
            }}
          >
            <Button
              variant="contained"
              onClick={() => {
                handleSave();
                setIsOpen(false);
              }}
              endIcon={<CachedIcon />}
              sx={{
                fontFamily: fonts.poppins,
                backgroundImage: colors.button,
                width: isMobile ? "100%" : "auto",
              }}
            >
              Update
            </Button>
            <Button
              onClick={() => setIsOpen(false)}
              sx={{
                fontFamily: fonts.poppins,
                color: colors.primary_dark,
                width: isMobile ? "100%" : "auto",
              }}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{ fontFamily: fonts.poppins, color: colors.primary_dark }}
        >
          Add New Seller Plan
        </DialogTitle>
        <DialogContent>
          <Paper sx={{ padding: "14px" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Plan Name"
                  name="name"
                  value={newPlan.name}
                  onChange={handleChange}
                  fullWidth
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Original Price"
                  name="originalPrice"
                  value={newPlan.originalPrice}
                  onChange={handleChange}
                  fullWidth
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="Discounted Price"
                  name="discountedPrice"
                  value={newPlan.discountedPrice}
                  onChange={handleChange}
                  fullWidth
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  label="No of Hours"
                  name="sponseredHrs"
                  value={newPlan.sponseredHrs}
                  onChange={handleChange}
                  fullWidth
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ padding: "14px" }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleClose}
            sx={{ fontFamily: fonts.poppins, backgroundImage: colors.button }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            color="primary"
            onClick={handleAdd}
            startIcon={<Add />}
            sx={{ fontFamily: fonts.poppins, backgroundImage: colors.button }}
          >
            Add Plan
          </Button>
        </DialogActions>
      </Dialog>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "stretch",
          justifyContent: "center",
          flexWrap: "wrap",
          marginBottom: 5,
          gap: 3,
        }}
      >
        {plans.map((plan) => (
          <Paper
            elevation={0}
            sx={{
              padding: 4,
              width: isMobile ? "100%" : "calc(25% - 24px)",
              minWidth: isMobile ? "100%" : "280px",
              borderRadius: 5,
              border: `1px solid ${colors.border}`,
              background: "#FFFFFF",
              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.03)",
              transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "4px",
                background: colors.primary,
                opacity: 0,
                transition: "opacity 0.3s ease"
              },
              "&:hover": {
                transform: "translateY(-10px)",
                boxShadow: "0 20px 40px rgba(0, 0, 0, 0.08)",
                borderColor: colors.primary,
                "&::before": {
                  opacity: 1
                }
              },
              marginBottom: isMobile ? 3 : 0,
            }}
          >
            <Box key={plan._id}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: fonts.poppins,
                    fontSize: "1.25rem",
                    fontWeight: 700,
                    color: colors.primary_dark,
                    mb: 1
                  }}
                >
                  {plan.name}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flexGrow: 1,
                  mt: 1
                }}
              >
                <Box sx={{ mb: 4 }}>
                  <Typography
                    sx={{
                      fontFamily: fonts.poppins,
                      fontSize: "0.85rem",
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      color: colors.text,
                      opacity: 0.6,
                      fontWeight: 600,
                      mb: 1,
                    }}
                  >
                    Usage Limits
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ 
                      width: 40, 
                      height: 40, 
                      borderRadius: "12px", 
                      bgcolor: "rgba(73, 167, 76, 0.1)", 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center",
                      color: colors.primary
                    }}>
                      <CachedIcon sx={{ fontSize: "1.2rem" }} />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontFamily: fonts.poppins,
                          fontSize: "1.25rem",
                          fontWeight: 700,
                          color: colors.primary_dark,
                          lineHeight: 1
                        }}
                      >
                        {plan.sponseredHrs} Hrs
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: fonts.poppins,
                          fontSize: "0.75rem",
                          color: colors.text,
                          opacity: 0.7
                        }}
                      >
                        Sponsored Boost
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                
                <Box sx={{ mt: "auto", pt: 4, borderTop: `1px solid ${colors.border}` }}>
                  <Typography
                    sx={{
                      fontFamily: fonts.poppins,
                      fontSize: "2rem",
                      fontWeight: 800,
                      color: colors.primary_dark,
                      display: "flex",
                      alignItems: "baseline",
                      gap: 1.5,
                      lineHeight: 1
                    }}
                  >
                    <Typography component="span" sx={{ fontSize: "1.25rem", fontWeight: 700 }}>₹</Typography>
                    {plan.discountedPrice}
                    <Typography
                      component="span"
                      sx={{
                        textDecoration: "line-through",
                        color: colors.text,
                        opacity: 0.4,
                        fontFamily: fonts.poppins,
                        fontSize: "1rem",
                        fontWeight: 500
                      }}
                    >
                      ₹{plan.originalPrice}
                    </Typography>
                  </Typography>
                  <Typography sx={{ 
                    fontFamily: fonts.poppins, 
                    fontSize: "0.75rem", 
                    color: colors.text, 
                    opacity: 0.6,
                    mt: 1
                  }}>
                    One-time purchase
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 1,
                  marginTop: 4,
                }}
              >
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundImage: colors.button,
                    borderRadius: 2,
                    py: 1.5,
                    textTransform: "none",
                    fontSize: "1rem",
                    fontWeight: 600,
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  }}
                  onClick={() => handleEdit(plan)}
                  startIcon={<EditIcon />}
                >
                  Edit Plan
                </Button>

                {/* <Button
                  variant="contained"
                  sx={{
                    backgroundImage: colors.button,
                    borderRadius: isMobile ? 5 : 10,
                    width: isMobile ? "100%" : "auto",
                  }}
                  onClick={() => handleDelete(plan._id)}
                >
                  <DeleteIcon />
                </Button> */}
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
};

export default SellerPlan;
